import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider, Helmet } from 'react-helmet-async';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { BucketeerProvider } from '@bucketeer/react-client-sdk';
import App from '@/entrypoints/main/App';
import './index.css';
import { setupChunkLoadingErrorHandler } from '@/utils/chunkLoadingErrorHandler';
import { setupGlobalErrorHandler, isAutoReportingEnabled } from '@/utils/globalErrorHandler';
import ChunkLoadErrorBoundary from '@/components/ChunkLoadErrorBoundary';
import ReportingErrorBoundary from '@/components/ReportingErrorBoundary';
import { initClarity } from '@/utils/clarity';
import { initFrameProtection } from '@/utils/frameProtection';
import { isAuthRoute } from '@/utils/sensitiveUrl';
import '@/utils/mixpanel';
import {
  bucketeerConfig,
  getBucketeerAnonymousId,
  createBucketeerUser,
  initializeBKTClient,
  getBKTClient,
  destroyBKTClient,
} from '@/utils/bucketeer';
import { useUserStore } from '@/stores/user/userStore';

const helmetContext = {};

// Initialize frame protection early to prevent clickjacking
initFrameProtection();

// Setup global chunk loading error handler only in production
if (window.location.hostname !== 'localhost') {
  setupChunkLoadingErrorHandler();
}

// Setup global JS error handler for auto-reporting
// Enabled in production, or locally with VITE_ENABLE_LOCAL_ERROR_REPORTING=true
if (isAutoReportingEnabled()) {
  setupGlobalErrorHandler();
}

// Initialize Microsoft Clarity
initClarity();

function Root() {
  const [client, setClient] = useState(null);
  // Order 7170: no third-party script while an auth link is open. Cello's app
  // script loads once index.html's tag loader reports the user left the route.
  const [onAuthRoute, setOnAuthRoute] = useState(() => isAuthRoute(window.location.pathname));
  useEffect(() => {
    if (!onAuthRoute) return undefined;
    const leave = () => setOnAuthRoute(false);
    window.addEventListener('dante:left-auth-route', leave, { once: true });
    return () => window.removeEventListener('dante:left-auth-route', leave);
  }, [onAuthRoute]);
  const user = useUserStore(state => state.user);
  const bucketeerUserId = user?.id || getBucketeerAnonymousId();

  useEffect(() => {
    if (!bucketeerConfig) {
      console.log('[Bucketeer] Not configured (missing VITE_APP_BUCKETEER_API_KEY)');
      return;
    }
    const init = async () => {
      try {
        const bktUser = createBucketeerUser(bucketeerUserId);
        await initializeBKTClient(bucketeerConfig, bktUser);
        const bktClient = getBKTClient();
        setClient(bktClient);
        console.log('[Bucketeer] Client ready', bktClient ? '(evaluations loaded)' : '(null)');
      } catch (error) {
        setClient(getBKTClient());
        console.error('[Bucketeer] Init error:', error);
      }
    };
    init();
    return () => destroyBKTClient();
  }, [bucketeerUserId]);

  return (
    <ChunkLoadErrorBoundary>
      <ReportingErrorBoundary>
        <GoogleOAuthProvider clientId={import.meta.env.VITE_APP_GOOGLE_CLIENT_ID}>
          <BucketeerProvider client={client}>
            <HelmetProvider context={helmetContext}>
              {!onAuthRoute && (
                <Helmet>
                  <script type="module" src="https://assets.cello.so/app/latest/cello.js" async></script>
                </Helmet>
              )}
              <App />
            </HelmetProvider>
          </BucketeerProvider>
        </GoogleOAuthProvider>
      </ReportingErrorBoundary>
    </ChunkLoadErrorBoundary>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // <React.StrictMode>
  <Root />
  // </React.StrictMode>
);
